import BaseService from '../../base/base_service';
import {CGP_ERROR_CODES} from '../../exceptions/cgp_error_codes';
import Device from '../../utils/device/device';
import Strings from '../../utils/strings';
import _ from '../../utils/underscore';
import XHR, { IHttpHeaders, IHttpParams, IXHRSuccessResponse, IXHRErrorResponse } from './../../utils/xhr';
import IHttp2Service from './http2_service_interface';
import Store from '../../utils/store';
import {IObservableSubscription} from "../../utils/observable";
import {SYSTEM_EVENTS} from "../../events/events";

const CLIENT_IPs_STORAGE_KEY: string = "ClientIPs";
const SHOULD_SEND_IPs_STORAGE_KEY: string = "shouldSendIPs";

export interface Http2ServiceParams {
    origin: string;
}

export interface IRequestData
{
    params: IHttpParams;
    headers: IHttpHeaders;
    url: string;
    method: string;
}


export default class Http2Service extends BaseService implements IHttp2Service {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'Http2Service';

    static DEFAULT_SUCCESS_RESPONSE: IXHRSuccessResponse = {
        status: 200,
        data: '',
        xhr: null,
    };

    static DEFAULT_ERROR_RESPONSE: IXHRErrorResponse = {
        status: 500,
        statusText: 'Internal error',
        data: '',
    };

    private _origin: string = '';
    private _ipResponseTimeout:IObservableSubscription;

    init(params: Http2ServiceParams) {
        super.init(params);

        this.onSystemEvent(SYSTEM_EVENTS.GEO_LOCATION_FAILED, this._resetIPsList);

        this._origin = params.origin || window.location.origin;
        this._setShouldSendIPs(true, true);
        this._clearIPsFromLocalStorage();
    }


    /**
     * HTTP post request you can send custom params and headers.
     */

    post<T>(url: string = '', params: IHttpParams = {}, headers: IHttpHeaders = {}): Promise<T> {
        headers['Content-Type'] = 'application/x-www-form-urlencoded';
        const buildURL = this.originReplacer(url);
        return this._request(buildURL, params, 'post', headers);
    }

    fetch(url: string = '', method: string = 'POST', params = {}, headers: IHttpHeaders = {}): Promise<any>{
        return fetch(url, {
            method: method,
            headers: headers,
            body: JSON.stringify(params)
        });
    }


    /**
     * HTTP get request you can send custom params and headers.
     */
    get<T>(url: string = '', requestData: IHttpParams = {}, requestHeaders: IHttpHeaders = {}): Promise<T> {
        let buildURL = this.originReplacer(url);
        const queryStr = XHR.getQueryString(requestData);
        if (queryStr) {
            buildURL += '?' + queryStr;
        }
        return this._request(buildURL, {}, 'get', requestHeaders);
    }

    getOrigin(): string {
        return this._origin;
    }


    /**
     * Set the origin of the request from the configuration if not something else set.
     */
    public originReplacer(url: string): string {
        const stringUtil = new Strings(url);
        const shouldReplaceOrigin = stringUtil.startsWith('http://') || stringUtil.startsWith('https://');

        if (!shouldReplaceOrigin) {
            return this.getOrigin() + url;
        }

        return url;
    }


    /**
     * Bake and fire the request.
     */
    private _request(url: string, params: IHttpParams, method: string, headers: IHttpHeaders): Promise<any> {
        const req = this._readyRequest(params, headers);

        const requestData = this._createRequestData(url, params, headers, method)
        return this._goRequest(req[method](url), requestData);
    }

    /**
     * Create request data to be logged in case of Request Error
     */
    private _createRequestData(url: string, params: IHttpParams = {}, headers: IHttpHeaders = {}, method: string): IRequestData {
        // remove password. Security issue
        if (params && params['password']) {
            delete params['password'];
        }
        return Object.assign({},
            {
                params: params,
                headers: headers,
                url: url,
                method: method
            }
        );
    }


    private _goRequest(reqPromise: Promise<IXHRSuccessResponse>, reqData: IRequestData): Promise<any> {
        return reqPromise
            .then((response: IXHRSuccessResponse) => {
                if (!response) {
                    response = Http2Service.DEFAULT_SUCCESS_RESPONSE;
                }
                return this._responseSuccess(response, reqData);
            })
            .catch((errorResponse: IXHRErrorResponse) => {
                if (!errorResponse) {
                    errorResponse = Http2Service.DEFAULT_ERROR_RESPONSE;
                }
                throw this._responseError(errorResponse, reqData);
            });
    }


    /**
     * Get the ready request xhr object ready to fire!
     */
    private _readyRequest(params: IHttpParams = {}, headers: IHttpHeaders = {}): XHR {
        // Create an xhr instance per request.
        const req = new XHR();

        // Return the xhr instance with the custom headers and request payload params.
        return req
            .setHeaders(headers)
            .setParams(Object.assign({}, params, this._applicationParams()));
    }


    /**
     * Get essential application params depend on request.
     */
    private _applicationParams() {
        const params: any = {};

        // Set the current application iso3 language.
        params.lang = this.serviceFactory.languageService().getISO3();

        // Get the token bearer authorization to set on the header.
        const getBearerToken = this.serviceFactory.oAuth2Service().getTokenAuthorizeHeader();

        // Is token bearer exist? if yes send it with the request.
        if (getBearerToken) {
            params.Authorization = getBearerToken;
        }
        params.clientRequestId = Device.generateGuid().replace(/-/g, '');
        params.clientProperties = JSON.stringify(this._getClientProperties());


        const fakeClientIp = this.serviceFactory.oAuth2Service().getFakeClientIP();
        if(fakeClientIp) {
            params.FakeClientIP = fakeClientIp;
        }

        const cmsSource = this.serviceFactory.integrationService().getCmsSourceIfValid();
        if(cmsSource) {
            params.CGP_CMSSource = cmsSource;
        }
        params.CGP_DomainOrigin = window.location.origin;

        try {
            if(this._shouldSendIPs) {
                params.clientIPs = this._getClientIPs().toString();
                this._setShouldSendIPs(false);
                this._ipResponseTimeout = this.serviceFactory.observableFactoryService().fromTimeout(20000)
                    .forEach(() => {
                        this._setShouldSendIPs(true);
                        this._disposeIPRequestTimer();
                    })
            }
        }
        catch (err){
            this.logger.error("Exception when trying to add IP parameter to request", err);
        }

        return params;
    }

    private get _shouldSendIPs():boolean{
        const { value, timestamp } = _.safeJSON(Store.get(SHOULD_SEND_IPs_STORAGE_KEY), {}) || Object.assign({});
        if (!_.isNullOrUndefined(value)){
            if(Date.now()-timestamp<30000)//not older than 30 scall
            {
                return value;
            }
            else
            {
                return true;
            }
        }
        return true;
    }

    private _setShouldSendIPs(value:boolean, setOnlyIfExpired:boolean = false){
        if(setOnlyIfExpired === true){
            const { timestamp } = _.safeJSON(Store.get(SHOULD_SEND_IPs_STORAGE_KEY), {}) || Object.assign({});
            if (!_.isNullOrUndefined(timestamp)){
                if(Date.now()-timestamp<30000)//"not older than 30 s"
                {
                    return;
                }
            }
        }
        Store.set(SHOULD_SEND_IPs_STORAGE_KEY, JSON.stringify({value:value,timestamp:Date.now()}));
    }

    private  _getClientIPs():Array<string>{
        let clientIPs:Array<string>;
        try {
            clientIPs = _.safeJSON(Store.get(CLIENT_IPs_STORAGE_KEY), null);
        }
        catch (err){
            console.log("Couldn't get IPs list from local storage.")
        }

        if(_.isArray(clientIPs)){
            return clientIPs;
        }
        else{
            return [];
        }
    }

    private _addClientIP(clientIP:string){
        const clientIPs = this._getClientIPs();
        clientIPs.push(clientIP);
        Store.set(CLIENT_IPs_STORAGE_KEY, JSON.stringify(clientIPs));
    }

    private _getClientProperties() {
        const skinService = this.serviceFactory.skinService();
        return {
            version: skinService.getVersion(),
            brandName: skinService.getBrandName(),
            subBrandId: skinService.getSubBrandID(),
            brandId: skinService.getBrandID(),
            productPackageId: skinService.getProductPackageID(),
            screenWidth: Device.viewport.screenWidth,
            screenHeight: Device.viewport.screenHeight,
            language: this.serviceFactory.languageService().getISO3(),
            operatingSystem: Device.os.name
        };
    }


    /**
     * Cook and bake the success response ;)
     */
    private _responseSuccess(successResponse: IXHRSuccessResponse, requestData: IRequestData): any {

        try {
            if (successResponse.xhr && (this._shouldSendIPs === false)) {
                if (successResponse.xhr.getResponseHeader) {
                    let currentClientIP: string = successResponse.xhr.getResponseHeader("current-client-request-ip");
                    if (currentClientIP) {
                        if(this._getClientIPs().indexOf(currentClientIP) === -1) {
                            this._addClientIP(currentClientIP);
                        }
                        this._setShouldSendIPs(true);
                        this._disposeIPRequestTimer();
                    }
                }
                else {
                    if (!Store.get('headerFeatureSupportReported')) {
                        this.serviceFactory.structuredLoggingService()
                            .getLogger()
                            .addCategory("feature_support")
                            .addAction("getResponseHeader")
                            .addValue("not_supported")
                            .warn();
                        Store.set('headerFeatureSupportReported', 'true');
                    }
                }
            }
        }
        catch (err){
            this.logger.error("Exception when trying to get current-client-request-ip from response headers", err);
        }

        // Is the success response is valid?
        if (_.isNull(successResponse) || _.isUndefined(successResponse)) {
            this.logger.error(`url --> ${requestData.url}`, 'Success response is not defined server problem!', {errorRequest: requestData});
            successResponse = Http2Service.DEFAULT_SUCCESS_RESPONSE;
        }

        // Is the success response came with no data?
        if (!successResponse.data) {
            return {};
        }

        // Is the success response status exist?
        if (successResponse.status) {
            this.logger.silly('Success response return with status code --> ', successResponse.status);
        }

        // Parse safely the success response data if there is problem in the parsing return empty object `{}`.
        return _.safeJSON(successResponse.data, {});
    }


    /**
     * Cook and bake error response ;)
     */
    private _responseError(errorResponse: IXHRErrorResponse, requestData: IRequestData) {
        let errorData = `url --> ${requestData.url}`;
        let errorMsg = '';
        // Is the error response is valid?
        if (_.isNull(errorResponse) || _.isUndefined(errorResponse)) {
            this.logger.silly('Error response is not defined server problem!');
            errorResponse = Http2Service.DEFAULT_ERROR_RESPONSE;
            this.logger.silly('Client http request error handling when server not support build the error => ', errorResponse);
        }

        let parsedResponse: any = {};
        // Is error response came with no data?
        if (!errorResponse.data) {
            errorMsg +='Response back with unhandled data response! and error code!';
            this.logger.silly(errorData, errorMsg);
            parsedResponse.error_code = CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString();
        } else {
            parsedResponse = _.safeJSON(errorResponse.data, {error_code: CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString()});
            if (parsedResponse.error && parsedResponse.error_description) {
                //this happens for token request where the response contains more error information inside error_description field
                let innerResponse = _.safeJSON(parsedResponse.error_description, null);
                if(innerResponse && innerResponse.error_code) { // we have error information inside error_description so we use that
                    parsedResponse = innerResponse;
                }

            }
        }

        // Is there no error code error response?
        if (!parsedResponse.error_code) {
            this.logger.silly('Status code is undefined, default behavior set the status to 500.');
            parsedResponse.error_code = CGP_ERROR_CODES.GENERAL_ERROR_SUPPORT.toString();
        }

        // print all possible information about request / response
        if (errorResponse.status) {
            errorData += ` status --> ${errorResponse.status}`;
        }
        if (errorResponse.statusText) {
            errorData += ` statusText --> ${errorResponse.statusText}`;
        }
        errorMsg = 'Response return with an error';
        let responseLog = {
            errorResponse: errorResponse,
            errorRequest: requestData
        };

        let authenticationData: any = responseLog.errorRequest?.params?.authenticationData;
        if(authenticationData) {
            try {
                authenticationData = JSON.parse(authenticationData);
                if(authenticationData?.credentials) {
                    authenticationData.credentials.password = '*';
                    responseLog.errorRequest.params.authenticationData = authenticationData;
                }
            }catch(err){}
        }

        this.logger.error(errorData, errorMsg, responseLog);

        return parsedResponse;
    }

    private _disposeIPRequestTimer():void {
        this._ipResponseTimeout?.dispose();
        this._ipResponseTimeout = null;
    }

    private _resetIPsList():void{
        this._clearIPsFromLocalStorage();
        this._setShouldSendIPs(true);
    }

    private _clearIPsFromLocalStorage(){
        Store.del(CLIENT_IPs_STORAGE_KEY);
    }

}
